// Client-side source-document text extraction for the Solution Studio Sources tab.
//
// The Design-generation worker reads each source doc's text from S3 (`getObjectText`), so binary
// formats (PDF / Word / Excel) are extracted to plain text *here*, in the browser, and only the text
// is uploaded (`POST /projects/{id}/sources`). That keeps the upload path text-only — no S3 CORS /
// presigned PUT, no server-side parsers.
//
// Deps: `pdfjs-dist` (PDF) and `mammoth` (.docx). Excel/CSV reuse the already-present `xlsx`
// (SheetJS). We import the *legacy* pdf.js build and *browser* mammoth build because react-scripts 5
// (webpack 5 + babel-preset-react-app) chokes on the modern builds' `import.meta` / top-level await
// and on mammoth's Node-only main entry.

import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import mammoth from 'mammoth/mammoth.browser';
import * as XLSX from 'xlsx';

// pdf.js needs a worker. It is served SAME-ORIGIN from `public/pdfjs/` (staged by
// `scripts/copy-pdf-worker.js` on postinstall/prestart/prebuild, so it's always version-matched to the
// bundled pdfjs-dist) — NOT from a CDN. A runtime CDN fetch breaks PDF ingest behind locked-down
// networks / a strict CSP; a same-origin asset works everywhere. `PUBLIC_URL` respects a non-root
// deploy base; it is '' in dev + a normal root deploy.
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${process.env.PUBLIC_URL || ''}/pdfjs/pdf.worker.min.mjs`;

// Roughly mirrors the backend MAX_SOURCE_TEXT_CHARS guard so we fail before a doomed upload.
export const MAX_SOURCE_TEXT_CHARS = 4_000_000;

// Map an extension to a coarse `kind` (used for the list badge + accept filter).
const KIND_BY_EXT = {
  pdf: 'pdf',
  docx: 'docx',
  xlsx: 'xlsx', xls: 'xlsx',
  csv: 'text', txt: 'text', md: 'text', markdown: 'text', json: 'text', log: 'text',
};

export const ACCEPTED_EXTENSIONS = Object.keys(KIND_BY_EXT);
export const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(',');

function extOf(filename) {
  const m = /\.([^.]+)$/.exec(filename || '');
  return m ? m[1].toLowerCase() : '';
}

async function extractPdf(file) {
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const parts = [];
  for (let i = 1; i <= pdf.numPages; i += 1) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    parts.push(content.items.map((it) => it.str).join(' '));
  }
  return parts.join('\n\n');
}

async function extractDocx(file) {
  const arrayBuffer = await file.arrayBuffer();
  const { value } = await mammoth.extractRawText({ arrayBuffer });
  return value || '';
}

async function extractXlsx(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  // One CSV block per sheet, headed by the sheet name, so the LLM keeps tabular context.
  return wb.SheetNames
    .map((name) => `# Sheet: ${name}\n${XLSX.utils.sheet_to_csv(wb.Sheets[name])}`)
    .join('\n\n');
}

/**
 * Extract plain text from a picked File.
 * @returns {Promise<{ text: string, kind: string, contentType: string }>}
 * @throws {Error} on unsupported type, empty extraction, or text over the size cap.
 */
export async function extractText(file) {
  const ext = extOf(file.name);
  const kind = KIND_BY_EXT[ext];
  if (!kind) {
    throw new Error(`Unsupported file type ".${ext}". Allowed: ${ACCEPTED_EXTENSIONS.join(', ')}.`);
  }

  let text;
  if (kind === 'pdf') text = await extractPdf(file);
  else if (kind === 'docx') text = await extractDocx(file);
  else if (kind === 'xlsx') text = await extractXlsx(file);
  else text = await file.text(); // csv / txt / md / json

  text = (text || '').trim();
  if (!text) {
    throw new Error('No text could be extracted (the file may be empty, scanned, or image-only).');
  }
  if (text.length > MAX_SOURCE_TEXT_CHARS) {
    throw new Error(`Extracted text is too large (${text.length.toLocaleString()} chars; max ${MAX_SOURCE_TEXT_CHARS.toLocaleString()}).`);
  }
  return { text, kind, contentType: file.type || 'application/octet-stream' };
}
